import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader, ArrowRight, ArrowLeft } from 'lucide-react';
import api from '../utils/api';

const Login = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await api.post('/auth/public/login', formData);

            if (response.data.success) {
                const { token } = response.data.data;
                localStorage.setItem('authToken', token);
                // Forced redirect to reload App.tsx state
                window.location.href = '/admin';
            } else {
                setError(response.data.message || 'Invalid credentials');
            }
        } catch (err: any) {
            console.error("Login error", err);
            if (err.response && err.response.status === 401) {
                setError('Incorrect email or password.');
            } else {
                setError('Connection error. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex w-full font-sans bg-white">

            {/* LEFT SECTION: Emotional Image (Desktop Only) */}
            <div className="hidden lg:flex lg:w-1/2 relative bg-black">
                {/* Background Image */}
                <img
                    src="https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069&auto=format&fit=crop"
                    alt="Another Home Office"
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                />

                {/* Content Overlay */}
                <div className="relative z-10 p-12 flex flex-col justify-between h-full text-white">
                    <div className="flex items-center gap-3">
                        {/* UPDATED LOGO HERE */}
                        <img
                            src="/logo1.webp"
                            alt="Another Home Logo"
                            className="h-10 w-auto object-contain"
                        />
                        <span className="font-serif font-bold tracking-wide text-lg">ANOTHER HOME</span>
                    </div>

                    <div className="max-w-md">
                        <h1 className="font-serif text-5xl font-medium leading-tight mb-6">
                            Hospitality excellence starts here.
                        </h1>
                        <p className="text-lg text-white/80 font-light leading-relaxed">
                            Manage your exclusive properties, track reservations, and deliver unforgettable experiences to your guests through our unified dashboard.
                        </p>
                    </div>

                    {/* UPDATED FOOTER TEXT HERE */}
                    <div className="text-sm text-white/50">
                        Created by Axiom Group
                    </div>
                </div>
            </div>

            {/* RIGHT SECTION: Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white animate-fade-in">
                <div className="max-w-[420px] w-full">

                    {/* Back to Home Button */}
                    <button
                        onClick={() => navigate('/')}
                        className="mb-10 flex items-center gap-2 text-sm font-semibold text-gray-400 hover:text-primary transition-colors group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        Back to website
                    </button>

                    <div className="mb-10">
                        <h2 className="text-4xl font-serif font-bold text-primary mb-3">Welcome Back</h2>
                        <p className="text-gray-500">Please enter your credentials to access the admin dashboard.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="bg-red-50 text-brand text-sm p-4 rounded-xl border border-red-100 flex items-start gap-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-brand mt-2 shrink-0"></div>
                                {error}
                            </div>
                        )}

                        <div className="space-y-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 ml-1">Business Email</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-brand transition-colors" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="block w-full pl-12 pr-4 py-4 bg-gray-50 border border-transparent rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-brand/20 focus:border-brand transition-all font-medium"
                                        placeholder="admin@anotherhome.us"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 ml-1">Password</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-brand transition-colors" />
                                    </div>
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        className="block w-full pl-12 pr-4 py-4 bg-gray-50 border border-transparent rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-brand/20 focus:border-brand transition-all font-medium"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center items-center gap-3 py-4 px-6 border border-transparent rounded-2xl shadow-lg text-base font-bold text-white bg-brand hover:bg-[#c42e40] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-70 disabled:cursor-not-allowed transition-all transform active:scale-[0.98]"
                            >
                                {loading ? (
                                    <>
                                        <Loader className="w-5 h-5 animate-spin" />
                                        Verifying...
                                    </>
                                ) : (
                                    <>
                                        Sign In
                                        <ArrowRight className="w-5 h-5" />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Mobile Footer (UPDATED) */}
                    <div className="mt-12 text-center lg:hidden">
                        <p className="text-xs text-gray-400">Created by Axiom Group</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;